import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { DateInput } from "../../../utils/commonComponent/Custom/custominput";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
// import { dsQuery } from "../../datasheet/query";
import { certQuery } from "../../certificates/certQuery";
import { fetchMaterialNames } from "../../certificates/fetchHandler";
import moment from "moment/moment";
import StatusExcelDownload from "./statusExcelDownload";
import { getPermissions } from "../../rbac/hasAccess";
import { getFormattedDates } from "../../../utils/utils";

let notNeededColumn = ["id"];

// Fetch test names by testIds — same pattern as fetchMaterialNames
const fetchTestNames = async (ids = "") => {
  const unique = [...new Set(String(ids).split(",").map(s => s.trim()).filter(Boolean))];
  if (!unique.length) return "-";
  try {
    const rows = await fetchQuery(
      `SELECT id, testName FROM materialTestings WHERE id IN (${unique.join(",")}) AND status != -1`
    );
    return unique
      .map((id) => rows?.find((r) => String(r.id) === id)?.testName)
      .filter(Boolean)
      .join(", ") || "-";
  } catch (err) {
    console.error("fetchTestNames error:", err);
    return "-";
  }
};

const ApprovalStatusReport = (props) => {
  const permissions = getPermissions(props.moduleId);
  const [allGroupedData, setAllGroupedData] = useState([]);
  const [summaryReport, setSummaryReport] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searched, setSearched] = React.useState(false);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  // Pagination logic
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const handleChangePage = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      const paginated = allGroupedData.slice(newPage * rowsPerPage, (newPage + 1) * rowsPerPage);
      setSummaryReport(paginated);
      setCurrentPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (e) => {
    const newRows = parseInt(e.target.value, 10);
    setRowsPerPage(newRows);
    setCurrentPage(0);
    setSummaryReport(allGroupedData.slice(0, newRows));
  };


  const handleSearch = async () => {
    try {
      let baseQuery = `${certQuery} WHERE 1=1 AND certificate.status != -1 AND observationSheet.testedBy IS NOT NULL`;

      if (fromDate && toDate) {
        const { from, to } = getFormattedDates(fromDate, toDate);
        baseQuery += ` AND DATE(observationSheet.startTestingDate) >= '${from}' AND DATE(observationSheet.endTestingDate) <= '${to}'`;
      }

      baseQuery += ` ORDER BY id DESC`;

      const data = await fetchQuery(`${baseQuery};`);

      // Group rows by id in JavaScript
      const certMap = new Map();

      (data || []).forEach((row) => {
        const certId = row.id;

        if (!certMap.has(certId)) {
          certMap.set(certId, {
            inward_customerName: row.inward_customerName || "-",
            jobRegister_jobNumber: row.jobRegister_jobNumber || "-",
            inward_labReferenceNo: row.inward_labReferenceNo || "-",
            certificate_ulrNumber: row.certificate_ulrNumber || "-",
            certificate_reportNumber: row.certificate_reportNumber || "-",
            test_Date: row.observationSheet_startTestingDate
              ? moment(row.observationSheet_startTestingDate).format("DD/MM/YYYY")
              : "-",
            preparedBy_userName: row.preparedBy_userName || "-",
            reviewedBy_userName: row.reviewedBy_userName || "-",
            authorizedBy_userName: row.authorizedBy_userName || "-",
            _materialIds: new Set(),
            _testIds: new Set(),
          });
        }

        const cert = certMap.get(certId);

        // Collect unique materialIds — for fetchMaterialNames
        if (row.inwardTests_materialId) {
          String(row.inwardTests_materialId).split(",").map(s => s.trim()).filter(Boolean)
            .forEach(id => cert._materialIds.add(id));
        }

        // Collect unique testIds — for fetchTestNames
        if (row.inwardTests_testId) {
          String(row.inwardTests_testId).split(",").map(s => s.trim()).filter(Boolean)
            .forEach(id => cert._testIds.add(id));
        }
        // Also collect from observationSheet.testId as fallback
        if (row.observationSheet_testId) {
          String(row.observationSheet_testId).split(",").map(s => s.trim()).filter(Boolean)
            .forEach(id => cert._testIds.add(id));
        }
      });

      // Resolve material names and test names using fetch functions
      const allGrouped = await Promise.all(
        Array.from(certMap.values()).map(async (cert) => {
          const [materialNames, testNames] = await Promise.all([
            cert._materialIds.size
              ? fetchMaterialNames([...cert._materialIds].join(","))
              : "-",
            cert._testIds.size
              ? fetchTestNames([...cert._testIds].join(","))
              : "-",
          ]);

          return {
            inward_customerName: cert.inward_customerName,
            materialScope_MaterialName: materialNames || "-",
            materialTestings_testName: testNames || "-",
            jobRegister_jobNumber: cert.jobRegister_jobNumber || "-",
            inward_labReferenceNo: cert.inward_labReferenceNo || "-",
            certificate_ulrNumber: cert.certificate_ulrNumber || "-",
            certificate_reportNumber: cert.certificate_reportNumber || "-",
            test_Date: cert.test_Date || "-",
            preparedBy_userName: cert.preparedBy_userName || "-",
            reviewedBy_userName: cert.reviewedBy_userName || "-",
            authorizedBy_userName: cert.authorizedBy_userName || "-",
          };
        })
      );

      setAllGroupedData(allGrouped);
      setTotalRecords(allGrouped.length);
      setSummaryReport(allGrouped.slice(0, rowsPerPage));
      setCurrentPage(0);
      setSearched(true);
    } catch (error) {
      console.error("Error fetching summary report:", error);
    }
  };

  const clearSearch = () => {
    setSummaryReport([]);
    setAllGroupedData([]);
    setTotalRecords(0);
    setSearched(false);
    setFromDate(null);
    setToDate(null);
  };

  const isDisabled = !((fromDate && toDate));

  if(!permissions?.view) return;

  return (
    <div>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ width: "100%", p: 1,}}>
        <Box flexBasis="10%">
          <DateInput label="From Date" value={fromDate} onChange={(d) => {
              setFromDate(d);
              setSearched(false);
            }}
          />
        </Box>

        <Box flexBasis="10%">
          <DateInput label="To Date" value={toDate} onChange={(d) => {
              setToDate(d);
              setSearched(false);
            }}
          />
        </Box>

        <Box flexBasis="10%">
          <Button
            variant="contained"
            color={searched ? "error" : "primary"}
            disabled={isDisabled}
            onClick={() => searched ? clearSearch() : handleSearch()}
            fullWidth
            sx={{
              textTransform: "none",
              height: "40px",
            }}
          >
            Search
          </Button>
        </Box>

        {summaryReport?.length > 0 && (
          <Box flexBasis="30%" display="flex" gap={1}>
            {permissions?.excelDownload && (
              <StatusExcelDownload
                finalData={allGroupedData}
                notNeededColumn={notNeededColumn}
              />
            )}
          </Box>
        )}
      </Box>

      <div className="w-full border rounded-md shadow">
        {/* Table Container */}
        <div className="overflow-y-auto h-auto max-h-[400px]">
          <table className="min-w-full text-sm text-left text-gray-700">
            <thead className="sticky top-0 bg-blue-50 text-xs uppercase font-medium text-gray-600 z-10">
              <tr>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Sr. No.</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Customer Name</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Material Name</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Test Name</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Job Number</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Lab Ref No</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">ULR No</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Report No</th>
                <th className="px-4 py-3 text-center whitespace-nowrap capitalize">Test Date</th>
                <th className="px-4 py-3 text-center">Prepared By</th>
                <th className="px-4 py-3 text-center">Reviewed By</th>
                <th className="px-4 py-3 text-center">Authorized By</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {summaryReport.map((row, index) => {
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-center">{currentPage * rowsPerPage + index + 1}</td>
                    <td className="px-4 py-2 text-center">{row.inward_customerName}</td>
                    <td className="px-4 py-2 text-center truncate-text">{row.materialScope_MaterialName}</td>
                    <td className="px-4 py-2 text-center truncate-text">{row.materialTestings_testName}</td>
                    <td className="px-4 py-2 text-center">{row.jobRegister_jobNumber}</td>
                    <td className="px-4 py-2 text-center">{row.inward_labReferenceNo}</td>
                    <td className="px-4 py-2 text-center">{row.certificate_ulrNumber}</td>
                    <td className="px-4 py-2 text-center">{row.certificate_reportNumber}</td>
                    <td className="px-4 py-2 text-center">{row.test_Date}</td>
                    <td className="px-4 py-2 text-center">{row.preparedBy_userName}</td>
                    <td className="px-4 py-2 text-center">{row.reviewedBy_userName}</td>
                    <td className="px-4 py-2 text-center">{row.authorizedBy_userName}</td>
                  </tr>
                );
              })}

              {summaryReport.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center py-4 text-gray-500">
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 text-sm border-t">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              className="border rounded px-2 py-1 text-sm"
            >
              {[30, 50, 100].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600">
              {totalRecords === 0
                ? "0–0 of 0"
                : `${currentPage * rowsPerPage + 1}–${Math.min(
                    (currentPage + 1) * rowsPerPage,
                    totalRecords
                  )} of ${totalRecords}`}
            </span>
            <button
              onClick={() => handleChangePage(0)}
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <FirstPageIcon fontSize="small" />
            </button>
            <button
              onClick={() => handleChangePage(currentPage - 1)}
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </button>
            <button
              onClick={() => handleChangePage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </button>
            <button
              onClick={() => handleChangePage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-1 disabled:opacity-50"
            >
              <LastPageIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalStatusReport;